import { morph, MORPH_OPTIONS } from '@theme/morph';

class PackSizePicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    // Stop this from reaching the parent <variant-picker>, which would
    // otherwise treat it as a native option change and try to AJAX-fetch
    // a combined-listing style update using option value IDs that don't
    // exist on the target (separate-product) bundle.
    event.stopPropagation();

    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.dataset.navUrl) return;

    // Clear stale current/previous-checked flags before the morph runs. morph.js
    // force-preserves these two attributes from the old DOM onto the fetched page
    // (see PRESERVED_ATTRIBUTES in assets/morph.js), which is meant for the
    // same-page <variant-picker> flow where variant-picker.js keeps them in sync.
    // This picker never sets them itself, so without this the previously-selected
    // pack size's leftover data-current-checked="true" bleeds into the new page
    // alongside the newly-fetched page's own flag, leaving two "current" options
    // and breaking the sliding pill's sibling-selector positioning.
    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      radio.removeAttribute('data-current-checked');
      radio.removeAttribute('data-previous-checked');
    }

    this.#loadPage(target.dataset.navUrl);
  };

  /**
   * Fetches the target pack size's product page and morphs it into the
   * current page's <main>, so switching pack size feels like changing a
   * variant rather than navigating to a different page -- even though,
   * under the hood, each pack size is a separate Shopify product (bundles
   * require that on non-Plus plans, where Combined Listings isn't available).
   * @param {string} url
   */
  async #loadPage(url) {
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(url);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      const newMain = html.querySelector('main');
      const main = document.querySelector('main');
      if (!newMain || !main) return;

      morph(main, newMain, MORPH_OPTIONS);

      if (url !== window.location.pathname + window.location.search) {
        history.replaceState({}, '', url);
      }

      document.title = html.title;
    } catch (error) {
      console.error(error);
      // Fall back to a full navigation if the AJAX swap fails for any reason.
      window.location.href = url;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('pack-size-picker')) {
  customElements.define('pack-size-picker', PackSizePicker);
}
